import type { ProviderResponse, LocationResponse, AppointmentTypeResponse } from '@vagusx/clients';
import { getProviderClient, getLocationClient, getAppointmentTypeClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import { CACHE_TIME_5_MINS, queryClient } from '@/lib/query-client';

/**
 * The list/detail fetch + query-key contract for an entity picker. Single
 * source of truth shared by any single/multi picker built on {@link EntityCombobox},
 * so they compute identical query keys and share the React Query cache — they can
 * never drift apart.
 *
 * NB: VagusX list endpoints return the full catalog (no server `q=`/paging), so
 * these configs adapt `SearchResponse<T>` → `{ data, meta }` and are meant to be
 * used with the `preload` load strategy (fetch once, filter in memory).
 */
export interface EntityPickerConfig<T> {
  fetchList: (opts: {
    q?: string;
    limit: number;
  }) => Promise<{ data: T[]; meta?: { total: number } }>;
  fetchOne: (id: string) => Promise<{ data: T }>;
  listKey: (opts: { q?: string; limit: number }) => readonly unknown[];
  detailKey: (id: string) => readonly unknown[];
  getId: (item: T) => string;
  getLabel: (item: T) => string;
  /** Haystack for client-side ('preload') filtering — should include every
   *  field users type to find a row. Defaults to `getLabel`. */
  searchText?: (item: T) => string;
}

export const providerPickerConfig: EntityPickerConfig<ProviderResponse> = {
  fetchList: async () => {
    const r = await getProviderClient().list();
    return { data: r.items, meta: { total: r.total } };
  },
  fetchOne: async (id) => ({ data: await getProviderClient().getById(id) }),
  // list() takes no q/limit, so the key is stable regardless of opts. It must NOT
  // be queryKeys.providers.list() — that key belongs to useProviders(), which caches
  // a different shape (see queryKeys.providers.picker).
  listKey: () => queryKeys.providers.picker(),
  detailKey: (id) => queryKeys.providers.detail(id),
  getId: (p) => p.id,
  getLabel: (p) => p.displayName || `${p.firstName} ${p.lastName}`.trim(),
};

export const locationPickerConfig: EntityPickerConfig<LocationResponse> = {
  fetchList: async () => {
    const r = await getLocationClient().list();
    return { data: r.items, meta: { total: r.total } };
  },
  fetchOne: async (id) => ({ data: await getLocationClient().getById(id) }),
  listKey: () => queryKeys.locations.picker(),
  detailKey: (id) => queryKeys.locations.detail(id),
  getId: (l) => l.id,
  getLabel: (l) => l.name,
};

/**
 * Locations a patient can be registered under in the EHR: active and physical
 * (a virtual location has no EHR department), sorted by name. Read through the
 * location picker's cached catalog, so it costs no second list request.
 */
export const registrationLocationPickerConfig: EntityPickerConfig<LocationResponse> = {
  ...locationPickerConfig,
  fetchList: async (opts) => {
    const all = await queryClient.fetchQuery({
      queryKey: locationPickerConfig.listKey(opts),
      queryFn: () => locationPickerConfig.fetchList(opts),
      staleTime: CACHE_TIME_5_MINS,
    });
    const data = all.data
      .filter((l) => l.rowStatus === 0 && !l.isVirtual)
      .sort((a, b) => a.name.localeCompare(b.name));
    return { data, meta: { total: data.length } };
  },
  listKey: () => queryKeys.locations.registrationPicker(),
};

export const appointmentTypePickerConfig: EntityPickerConfig<AppointmentTypeResponse> = {
  fetchList: async () => {
    const data = await getAppointmentTypeClient().list();
    return { data, meta: { total: data.length } };
  },
  fetchOne: async (id) => ({ data: await getAppointmentTypeClient().getById(id) }),
  listKey: () => queryKeys.appointmentTypes.picker(),
  detailKey: (id) => queryKeys.appointmentTypes.detail(id),
  getId: (t) => t.id,
  getLabel: (t) => `${t.name} (${t.durationMinutes} min)`,
  searchText: (t) => t.name,
};
